import React, { Component } from "react";
import { AnalyticsQueryType, AthenaDatasetResult } from "../../../models/types";
import { DSVRowArray } from "d3";
import { loadCsv } from "../../../common/AnalyticsHelpers";
import { SegmentedProgressCircle } from "../../simple/analytics/SegnentedProgressCircle";
import { Card, Col } from "react-bootstrap";

interface State {
    csvData: DSVRowArray | undefined;
}

interface Props {
    data: AthenaDatasetResult[];
    darkMode?: boolean;
}

export class FunnelBasicDetails extends Component<Props> {
    state: State = {
        csvData: undefined
    };

    componentDidMount(): void {
        const { data } = this.props;
        const funnelData = data[0].dataset.find(
            element => element.type === AnalyticsQueryType.FunnelKeyValues
        );
        if (funnelData?.success && funnelData.message) {
            loadCsv(funnelData.message)
                .then(result => {
                    this.setState({ csvData: result });
                })
                .catch(error => console.log("[DEBUG] error ", error));
        }
    }

    render() {
        const { darkMode } = this.props;
        const { csvData } = this.state;

        if (!csvData || csvData.length === 0) {
            return null;
        }

        const { columns, ...cleanData } = csvData;
        let impressions: number | undefined = undefined;
        let ctaPresses: number | undefined = undefined;
        let firstTouches: number | undefined = undefined;
        let initialEngagement: number | undefined = undefined;
        let initialEngagementProgress: number | undefined = undefined;
        let deepEngagement: number | undefined = undefined;
        let deepEngagementProgress: number | undefined = undefined;
        let ctr: number | undefined = undefined;
        let ctrProgress: number | undefined = undefined;
        let ctrColor: string | undefined = undefined;

        for (const key in cleanData) {
            if (cleanData[key].eventName === "adStart") {
                impressions = Number(cleanData[key].events);
            }
            if (cleanData[key].eventName === "firstTouch") {
                firstTouches = Number(cleanData[key].events);
            }
            if (cleanData[key].eventName === "ctaBtnPressed") {
                ctaPresses = Number(cleanData[key].events);
            }
        }

        if (impressions && firstTouches) {
            initialEngagement = (firstTouches / impressions) * 100;
            initialEngagementProgress = 10 * (firstTouches / impressions);
        }
        if (ctaPresses && firstTouches) {
            deepEngagement = (ctaPresses / firstTouches) * 100;
            deepEngagementProgress = 10 * (ctaPresses / firstTouches);
        }
        if (impressions && ctaPresses) {
            ctr = (ctaPresses / impressions) * 100;
            ctrProgress = 10 * (ctaPresses / impressions);
            if (ctr < 2.5) {
                ctrColor = "#ff0000";
            } else if (ctr < 4) {
                ctrColor = "#ffD137FF";
            }
        }

        return (
            <Card className="text-inverse mt-2">
                <Card.Header className="text-inverse-75">
                    <h5>Key Values</h5>
                </Card.Header>
                <Card.Body className="d-flex">
                    <Col className="d-flex justify-content-center align-items-center">
                        {impressions ? (
                            <SegmentedProgressCircle
                                radius={59}
                                cx={80}
                                cy={80}
                                strokeWidth={5}
                                progress={10}
                                value={impressions.toLocaleString("en", {
                                    maximumFractionDigits: 2,
                                    notation: "compact",
                                    compactDisplay: "short"
                                })}
                                title="Impressions"
                                darkMode={darkMode || false}
                            />
                        ) : null}
                        {initialEngagement && initialEngagementProgress ? (
                            <SegmentedProgressCircle
                                radius={59}
                                cx={80}
                                cy={80}
                                strokeWidth={5}
                                progress={initialEngagementProgress}
                                value={`${initialEngagement.toFixed(1)}%`}
                                title="Initial"
                                titleSecondary="Engagement"
                                darkMode={darkMode || false}
                            />
                        ) : null}
                        {deepEngagement && deepEngagementProgress ? (
                            <SegmentedProgressCircle
                                radius={59}
                                cx={80}
                                cy={80}
                                strokeWidth={5}
                                progress={deepEngagementProgress}
                                value={`${deepEngagement.toFixed(1)}%`}
                                title="Deep"
                                titleSecondary="Engagement"
                                darkMode={darkMode || false}
                            />
                        ) : null}
                        {ctr && ctrProgress ? (
                            <SegmentedProgressCircle
                                radius={59}
                                cx={80}
                                cy={80}
                                strokeWidth={5}
                                progress={10}
                                value={`${ctr.toFixed(1)}%`}
                                title="CTR"
                                darkMode={darkMode || false}
                                circleColor={ctrColor}
                            />
                        ) : null}
                    </Col>
                    <Col>
                        <Card className="text-inverse inner-card my-1">
                            <Card.Header>Legend</Card.Header>
                            <Card.Body>
                                {impressions ? (
                                    <>
                                        <Card.Subtitle>
                                            Impressions
                                        </Card.Subtitle>
                                        <Card.Text>
                                            All the adStart events
                                        </Card.Text>
                                    </>
                                ) : null}
                                {initialEngagement &&
                                initialEngagementProgress ? (
                                    <>
                                        <Card.Subtitle>
                                            Initial Engagement
                                        </Card.Subtitle>
                                        <Card.Text>
                                            Events from adStart to firstTouch
                                        </Card.Text>
                                    </>
                                ) : null}
                                {deepEngagement && deepEngagementProgress ? (
                                    <>
                                        <Card.Subtitle>
                                            Deep Engagement
                                        </Card.Subtitle>
                                        <Card.Text>
                                            Events from firstTouch to
                                            ctaBtnPressed
                                        </Card.Text>
                                    </>
                                ) : null}
                                {ctr && ctrProgress ? (
                                    <>
                                        <Card.Subtitle>CTR</Card.Subtitle>
                                        <Card.Text>
                                            Calculated from adStart and
                                            ctaBtnPressed events
                                        </Card.Text>
                                    </>
                                ) : null}
                            </Card.Body>
                        </Card>
                    </Col>
                </Card.Body>
            </Card>
        );
    }
}
